import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { LeagueSelect } from "@/components/LeagueSelect";
import { TeamSelect } from "@/components/TeamSelect";
import { UploadCloud, FileSpreadsheet, Activity, FileText, Loader2, Trash2, CheckCircle2, Database } from "lucide-react";
import { toast } from "sonner";

const selectCls = "rounded-xl border border-white/10 bg-[#0a0a0a] px-4 py-3 text-sm text-white outline-none focus:border-cyan-400/50";
const KINDS = [
  { v: "gps", l: "GPS / Training load", icon: Activity, hint: "CSV columns: player, date, load (or player_load, or srpe+duration). Feeds real ACWR & injury risk.", accept: ".csv" },
  { v: "stats", l: "Custom player stats", icon: FileSpreadsheet, hint: "CSV columns: player, minutes, apps, rating. Folds into readiness, mindset & dossiers.", accept: ".csv" },
  { v: "doc", l: "Scout report / document", icon: FileText, hint: "PDF, XLSX, CSV or TXT. OCTON reads it and folds insights into this club's briefings.", accept: ".pdf,.xlsx,.xlsm,.csv,.txt" },
];

export default function DataIngest() {
  const [leagues, setLeagues] = useState([]);
  const [league, setLeague] = useState(39);
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState(null);
  const [kind, setKind] = useState("gps");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/leagues").then((r) => setLeagues(r.data)); }, []);
  useEffect(() => { api.get(`/teams?league=${league}`).then((r) => { setTeams(r.data); setTeamId(r.data[0]?.id); }); }, [league]);
  useEffect(() => { if (teamId) refresh(); /* eslint-disable-next-line */ }, [teamId]);

  const refresh = () => api.get(`/ingest/list?team_id=${teamId}`).then((r) => setInfo(r.data)).catch(() => {});
  const active = KINDS.find((k) => k.v === kind);

  const upload = async () => {
    if (!file || !teamId) { toast.error("Pick a team and a file"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("team_id", teamId); fd.append("kind", kind); fd.append("file", file);
    try {
      const r = await api.post("/ingest/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data.detail || "Ingested");
      setFile(null); if (fileRef.current) fileRef.current.value = "";
      refresh();
    } catch (e) {
      toast.error("Ingest failed — check the file format");
    } finally { setBusy(false); }
  };

  const clear = async (k) => {
    try { await api.post("/ingest/clear", { team_id: teamId, kind: k }); toast("Cleared"); refresh(); } catch { toast.error("Could not clear"); }
  };

  return (
    <div className="px-6 pb-24 pt-20 sm:px-10 lg:px-16" data-testid="data-ingest-page">
      <p className="font-tac text-[11px] tracking-[0.35em] txt-cyan">DATA INGEST · FEED THE BRAIN</p>
      <h1 className="font-head mt-2 text-5xl font-black uppercase tracking-tighter">Data Ingest</h1>
      <p className="mt-2 max-w-2xl text-sm text-white/50">Upload real club data — GPS/training load, custom player stats, or scout reports — and OCTON folds it straight into its readiness, ACWR, injury-risk and briefing analyses.</p>

      <div className="mt-6 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1"><label className="font-tac text-[9px] tracking-widest text-white/40">LEAGUE</label>
          <LeagueSelect testId="ingest-league" value={league} onChange={(id) => setLeague(id)} /></div>
        <div className="flex flex-col gap-1"><label className="font-tac text-[9px] tracking-widest text-white/40">CLUB</label>
          <TeamSelect testId="ingest-team" teams={teams} value={teamId} onChange={(id) => setTeamId(id)} /></div>
      </div>

      {/* kind chooser */}
      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        {KINDS.map((k) => { const I = k.icon; const on = kind === k.v; return (
          <button key={k.v} onClick={() => { setKind(k.v); setFile(null); if (fileRef.current) fileRef.current.value = ""; }} data-testid={`ingest-kind-${k.v}`}
            className={`rounded-2xl border p-4 text-left transition ${on ? "border-cyan-400/60 bg-cyan-400/[0.07]" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
            <I size={18} className={on ? "txt-cyan" : "text-white/50"} />
            <p className="mt-2 font-body text-sm font-semibold text-white">{k.l}</p>
            <p className="mt-1 text-[11px] leading-snug text-white/45">{k.hint}</p>
          </button>
        ); })}
      </div>

      {/* dropzone */}
      <div className="mt-4 rounded-2xl border border-dashed border-white/20 bg-black/30 p-6" data-testid="ingest-dropzone" data-octon-section="data ingest upload dropzone gps csv training" data-octon-label="Upload Dropzone">
        <div className="flex flex-wrap items-center gap-4">
          <input ref={fileRef} type="file" accept={active?.accept} onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ingest-file" className="text-sm text-white/70 file:mr-3 file:rounded-full file:border-0 file:bg-white/10 file:px-4 file:py-2 file:text-white/80" />
          <button onClick={upload} disabled={busy || !file} data-testid="ingest-upload-btn"
            className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-6 py-3 text-sm font-bold text-black transition hover:shadow-[0_0_30px_rgba(0,229,255,0.5)] disabled:opacity-40">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <UploadCloud size={16} />} Ingest into analyses
          </button>
        </div>
        <p className="mt-3 text-[11px] text-white/40">{active?.hint}</p>
      </div>

      {/* what's ingested */}
      {info && (
        <div className="mt-6" data-testid="ingest-summary" data-octon-section="ingest summary absorbed files" data-octon-label="Ingest Summary">
          <div className="grid grid-cols-3 gap-4">
            <div className="card-tac flex items-center justify-between p-4"><div><p className="font-tac text-[9px] tracking-widest text-white/40">GPS PLAYERS</p><p className="font-head text-3xl font-black txt-cyan">{info.counts.gps_players}</p></div>{info.counts.gps_players > 0 && <button onClick={() => clear("gps")} className="text-white/30 hover:text-red-400" data-testid="ingest-clear-gps"><Trash2 size={15} /></button>}</div>
            <div className="card-tac flex items-center justify-between p-4"><div><p className="font-tac text-[9px] tracking-widest text-white/40">STATS PLAYERS</p><p className="font-head text-3xl font-black" style={{ color: "#ccff00" }}>{info.counts.stats_players}</p></div>{info.counts.stats_players > 0 && <button onClick={() => clear("stats")} className="text-white/30 hover:text-red-400" data-testid="ingest-clear-stats"><Trash2 size={15} /></button>}</div>
            <div className="card-tac flex items-center justify-between p-4"><div><p className="font-tac text-[9px] tracking-widest text-white/40">DOCUMENTS</p><p className="font-head text-3xl font-black" style={{ color: "#ff7300" }}>{info.counts.docs}</p></div>{info.counts.docs > 0 && <button onClick={() => clear("doc")} className="text-white/30 hover:text-red-400" data-testid="ingest-clear-doc"><Trash2 size={15} /></button>}</div>
          </div>
          {info.log?.length > 0 && (
            <div className="mt-4 card-tac p-4" data-testid="ingest-log" data-octon-section="ingest log history" data-octon-label="Ingest Log">
              <div className="mb-2 flex items-center gap-2"><Database size={14} className="txt-cyan" /><span className="font-tac text-[10px] tracking-widest text-white/60">INGEST HISTORY</span></div>
              <div className="space-y-1.5">
                {info.log.map((e, i) => (
                  <div key={i} className="flex items-start gap-2 text-[12px]">
                    <CheckCircle2 size={13} className="mt-0.5 shrink-0 text-[#33d17a]" />
                    <span className="text-white/70">{e.summary}</span>
                    <span className="ml-auto shrink-0 font-mono text-[10px] text-white/30">{new Date(e.ts).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
